import type { BookedFlightItineraryWithDepartureTime } from "@b2bportal/air-booking-api";

export interface AirlineReference {
  airlineName: string;
  airlineCode: string;
  locator: string;
}

export interface BookingDetailsSectionState {
  confirmationCode: string;
  airlineReferences: AirlineReference[];
  travellers: Array<{
    nameNumber: string;
    givenName: string;
    surname: string;
  }>;
  contactPhone: string | null;
  contactEmail: string | null;
  frequentFlyerNumbers: Record<string, string>;
  hasFrequentFlyer: boolean;
}

export function useBookingDetailsSection(
  itinerary: BookedFlightItineraryWithDepartureTime
): BookingDetailsSectionState {
  const bookedItinerary = itinerary.bookedItinerary;
  const passengers = bookedItinerary.passengers;
  const travelItinerary = bookedItinerary.travelItinerary;

  // Get confirmation code (B2B locator)
  const confirmationCode = travelItinerary.locators?.b2b || "";

  // Get all airline references from segments (deduplicated by airline code + locator)
  const airlineReferencesMap = new Map<string, AirlineReference>();

  travelItinerary.slices?.forEach((slice) => {
    slice.segments?.forEach((segment) => {
      const locator = segment.marketingAirline?.locator;
      if (locator) {
        const key = `${segment.marketingAirline.code}-${locator}`;
        if (!airlineReferencesMap.has(key)) {
          airlineReferencesMap.set(key, {
            airlineCode: segment.marketingAirline.code,
            airlineName: segment.marketingAirline.code, // Will need airline map for display name
            locator,
          });
        }
      }
    });
  });

  const airlineReferences = Array.from(airlineReferencesMap.values());

  // Get contact information
  const contactInfo = passengers.contact.contact;
  const contactPhone = contactInfo.phoneNumber || null;
  const contactEmail = contactInfo.emailAddress || null;

  // Get travellers list
  const allPassengers = [
    ...(passengers.alone || []),
    ...(passengers.withLapInfants?.map((p) => p.adult) || []),
    ...(passengers.withLapInfants?.map((p) => p.infant) || []),
  ];

  const travellers = allPassengers.map((passenger) => ({
    nameNumber: passenger.nameNumber,
    givenName: passenger.person.givenName,
    surname: passenger.person.surname,
  }));

  // Collect all frequent flyer numbers from all passengers
  const frequentFlyerNumbers: Record<string, string> = {};
  allPassengers.forEach((passenger) => {
    const ffNumbers = passenger.person.frequentFlyer;
    if (ffNumbers && typeof ffNumbers === "object") {
      Object.entries(ffNumbers).forEach(([airline, ffData]) => {
        if (ffData && ffData.value) {
          // If the same airline appears for multiple passengers, keep the first
          if (!frequentFlyerNumbers[airline]) {
            frequentFlyerNumbers[airline] = ffData.value;
          }
        }
      });
    }
  });

  const hasFrequentFlyer = Object.keys(frequentFlyerNumbers).length > 0;

  return {
    confirmationCode,
    airlineReferences,
    travellers,
    contactPhone,
    contactEmail,
    frequentFlyerNumbers,
    hasFrequentFlyer,
  };
}
